import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PasswordInput from "../components/PasswordInput";
import { useAuth, API } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { subscriptionStatusView } from "../utils/subscriptionStatus";
import { useSeo } from "../lib/seo";

/**
 * /account/settings — Unified account settings hub.
 * Sections:
 *   1. Password & security (link to /account/security)
 *   2. Membership (business only — pause / resume / cancel)
 *   3. Booking availability (business only — pause / resume)
 *   4. Deactivate account (business only)
 *   5. Delete account (business only, 30-day grace)
 *   6. Trusted devices (link to /account/security section)
 * Customer users see a compact version (password + delete-account via support).
 */
export default function AccountSettings() {
  useSeo({ title: "Account settings — BookFreely", path: "/account/settings", noindex: true });
  const { user, authHeader, refreshUser, logout } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [confirmChk, setConfirmChk] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showCancelSub, setShowCancelSub] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteNote, setDeleteNote] = useState("");
  const [retention, setRetention] = useState(null);

  useEffect(() => {
    if (!user) return;
    // If URL contains ?tab=..., could deep-link. For MVP: single page.
  }, [user]);

  if (!user) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-md mx-auto px-6 py-16 text-sm" style={{ color: "var(--gf-text-muted)" }}>
          Please <button className="underline" onClick={() => nav("/login?next=/account/settings")}>sign in</button> to open your settings.
        </div>
        <Footer />
      </div>
    );
  }

  const isBiz = user.role === "business";
  const bookingsPaused = !!user.bookings_paused;
  const subActive = !!user.subscription_active;
  const accountStatus = user.account_status || "active";

  // Reason options for the delete-account dropdown differ by role.
  const deleteReasonOptions = isBiz
    ? [
        ["expensive", "Subscription is too expensive"],
        ["no_bookings", "Not getting enough bookings"],
        ["switching", "Switching platforms"],
        ["closing", "Closing my business"],
        ["technical", "Technical issues"],
        ["other", "Other"],
      ]
    : [
        ["no_longer_need", "I no longer need BookFreely"],
        ["privacy", "Privacy concerns"],
        ["duplicate", "I have another account"],
        ["not_useful", "Didn't find what I needed"],
        ["technical", "Technical issues"],
        ["other", "Other"],
      ];

  const call = async (url, body = {}, method = "post") => {
    const client = method === "delete" ? axios.delete : axios.post;
    return client(url, method === "delete" ? { headers: authHeader() } : body, { headers: authHeader() });
  };

  const togglePause = async () => {
    setBusy("pause");
    try {
      await call(`${API}/business/bookings/${bookingsPaused ? "resume" : "pause"}`);
      toast.success(bookingsPaused ? "Bookings resumed" : "Bookings paused — customers can't book new appointments");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update booking status");
    } finally { setBusy(""); }
  };

  const cancelSubscription = async () => {
    // DEPRECATED (Iteration 56, Feb 28 2026): the customer-facing cancellation
    // flow now lives inside the Stripe Customer Portal. Kept as a safety
    // fallback so previously-visible UI never explodes if a user still has
    // an old page open. New UX: `openBillingPortal` below.
    setBusy("cancel-sub");
    try {
      await call(`${API}/business/subscription/cancel`);
      toast.success("Subscription cancelled. It stays active until the end of your paid period.");
      setShowCancelSub(false);
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel subscription");
    } finally { setBusy(""); }
  };

  const openBillingPortal = async () => {
    // Iteration 56 (Feb 28 2026) — SINGLE source of truth for subscription
    // management. Businesses land in Stripe's Customer Portal where they can
    // update card, view invoices, change plan, or cancel. On return we
    // refreshUser() so the dashboard shows the new state within seconds
    // without a page reload.
    setBusy("manage-sub");
    try {
      const r = await axios.post(`${API}/subscription/portal`, {}, { headers: authHeader() });
      if (r.data?.url) {
        window.location.assign(r.data.url);
        return;
      }
      toast.error("Could not open Stripe portal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe portal");
    } finally { setBusy(""); }
  };

  const resumeSubscription = async () => {
    setBusy("resume-sub");
    try {
      await call(`${API}/business/subscription/resume`);
      toast.success("Subscription resumed. Auto-renew is back on.");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume subscription");
    } finally { setBusy(""); }
  };

  const deactivate = async () => {
    if (!confirmPw) return toast.error("Enter your password to confirm.");
    setBusy("deactivate");
    try {
      const url = isBiz ? `${API}/business/account/deactivate` : `${API}/customer/account/deactivate`;
      await axios.post(url, { password: confirmPw }, { headers: authHeader() });
      toast.success("Account deactivated. You can reactivate anytime.");
      setShowDeactivate(false); setConfirmPw("");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not deactivate");
    } finally { setBusy(""); }
  };

  const reactivate = async () => {
    setBusy("reactivate");
    try {
      const url = isBiz ? `${API}/business/account/reactivate` : `${API}/customer/account/reactivate`;
      await axios.post(url, {}, { headers: authHeader() });
      toast.success("Welcome back — your account is active again.");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reactivate");
    } finally { setBusy(""); }
  };

  const requestDelete = async (acceptOffer = false) => {
    if (!confirmPw) return toast.error("Enter your password to confirm.");
    if (!confirmChk) return toast.error("Check the acknowledgement box.");
    setBusy("delete");
    try {
      const url = isBiz ? `${API}/business/account/delete` : `${API}/customer/account/delete`;
      const body = isBiz
        ? { password: confirmPw, confirm: true, reason: deleteReason, note: deleteNote, accept_retention_offer: acceptOffer }
        : { password: confirmPw, confirm: true, reason: deleteReason, note: deleteNote };
      const r = await axios.post(url, body, { headers: authHeader() });
      if (r.data?.retention_offer) {
        setRetention(r.data.retention_offer);
        toast.info("We'd like to offer you an extension before you go.");
        return;
      }
      toast.success("Account scheduled for deletion in 30 days. Sign in anytime in that window to cancel.");
      setShowDelete(false); setConfirmPw(""); setConfirmChk(""); setRetention(null);
      logout(); nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not process deletion");
    } finally { setBusy(""); }
  };

  return (
    <div className="App" data-testid="account-settings-page">
      <Header hideSearch />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Account · Settings</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2" style={{ color: "var(--gf-text)" }}>Settings</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--gf-text-soft)" }}>
          Change your password, pause your bookings or membership, deactivate or delete your account. Signed in as <b style={{ color: "var(--gf-text)" }}>{user.email}</b>.
        </p>

        {/* 1 · Password & security */}
        <SettingsCard title="Password &amp; security" testid="settings-security">
          <p>Change your password and manage trusted devices.</p>
          <Link data-testid="settings-security-link" to="/account/security"
                className="inline-block mt-3 rounded-md border px-4 py-2 text-sm"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            Change password
          </Link>
        </SettingsCard>

        {/* Appearance — light / dark / match system */}
        <AppearanceCard />
        {isBiz && (
          <>
            {/* 2 · Membership — Iteration 56: single Manage Subscription action (Stripe portal) */}
            <SettingsCard title="Membership" testid="settings-membership">
              {(() => {
                const view = subscriptionStatusView(user);
                const toneColor = view.tone === "ok" ? "var(--gf-success)"
                                : view.tone === "warn" ? "#b8862a"
                                : view.tone === "bad" ? "#dc2626"
                                : "var(--gf-text-muted)";
                return (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid="settings-sub-status-pill" className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-[0.15em]"
                            style={{ borderColor: toneColor, color: toneColor, background: "transparent" }}>
                        {view.label}
                      </span>
                    </div>
                    <p className="mt-2" data-testid="settings-sub-status-message">{view.message}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {!subActive && view.label === "No subscription" && (
                        <Link to="/business/subscribe" data-testid="settings-subscribe-link"
                              className="rounded-md px-4 py-2 text-sm font-medium"
                              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                          Subscribe
                        </Link>
                      )}
                      {user.stripe_customer_id && (
                        <button data-testid="settings-manage-sub-btn"
                                onClick={openBillingPortal}
                                disabled={busy === "manage-sub"}
                                className="rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
                                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                          {busy === "manage-sub" ? "Opening…" : "Manage subscription"}
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] mt-3" style={{ color: "var(--gf-text-muted)" }}>
                      Update your card, view invoices, change plan, or cancel — all in Stripe&rsquo;s secure Customer Portal.
                    </p>
                  </>
                );
              })()}
            </SettingsCard>

            {/* 3 · Booking availability */}
            <SettingsCard title="Booking availability" testid="settings-bookings-pause">
              <p>
                {bookingsPaused
                  ? "Your bookings are paused — customers can't book new appointments right now."
                  : "You're accepting new bookings."}
              </p>
              <button data-testid="settings-pause-btn" onClick={togglePause} disabled={busy === "pause"}
                      className="mt-3 rounded-md border px-4 py-2 text-sm disabled:opacity-50"
                      style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
                {busy === "pause" ? "…" : bookingsPaused ? "Resume bookings" : "Pause bookings"}
              </button>
            </SettingsCard>
          </>
        )}

        {/* 4 · Deactivate account (both roles) */}
        <SettingsCard title="Deactivate account" testid="settings-deactivate">
          {accountStatus === "deactivated" ? (
            <>
              <p>Your account is <b style={{ color: "#dc2626" }}>deactivated</b>. {isBiz ? "Your listings are hidden and you can't accept bookings until you reactivate." : "You can't book services until you reactivate."}</p>
              <button data-testid="settings-reactivate-btn" onClick={reactivate} disabled={busy === "reactivate"}
                      className="mt-3 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
                      style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                {busy === "reactivate" ? "…" : "Reactivate account"}
              </button>
            </>
          ) : (
            <>
              <p>
                {isBiz
                  ? "Temporarily hide your listings and stop new bookings. You can reactivate anytime — nothing is deleted."
                  : "Temporarily disable your account. You won't be able to book new services until you reactivate. Nothing is deleted."}
              </p>
              {!showDeactivate ? (
                <button data-testid="settings-deactivate-btn" onClick={() => setShowDeactivate(true)}
                        className="mt-3 rounded-md border px-4 py-2 text-sm"
                        style={{ borderColor: "#dc2626", color: "#dc2626" }}>
                  Deactivate my account
                </button>
              ) : (
                <div data-testid="settings-deactivate-form" className="mt-3 space-y-3">
                  <PasswordInput placeholder="Confirm your password" value={confirmPw}
                         onChange={(e) => setConfirmPw(e.target.value)}
                         data-testid="settings-deactivate-password"
                         className="w-full bg-transparent border rounded-md px-3 py-2 text-sm"
                         style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}/>
                  <div className="flex gap-2">
                    <button data-testid="settings-deactivate-confirm" onClick={deactivate} disabled={busy === "deactivate"}
                            className="rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
                            style={{ background: "#dc2626", color: "#fff" }}>
                      {busy === "deactivate" ? "…" : "Deactivate"}
                    </button>
                    <button onClick={() => { setShowDeactivate(false); setConfirmPw(""); }}
                            className="rounded-md border px-4 py-2 text-sm"
                            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </SettingsCard>

        {/* 5 · Delete account (both roles) */}
        <SettingsCard title="Delete account" testid="settings-delete">
          <p>Permanently delete your account, with a 30-day grace period during which you can still sign in to cancel the deletion.</p>
          {!showDelete ? (
            <button
              data-testid="settings-delete-btn"
              onClick={() => {
                setShowDelete(true);
                // seed a sensible default reason for whichever role is signed in
                if (!deleteReason) setDeleteReason(deleteReasonOptions[0][0]);
              }}
              className="mt-3 rounded-md border px-4 py-2 text-sm"
              style={{ borderColor: "#dc2626", color: "#dc2626" }}>
              Delete my account
            </button>
          ) : (
            <div data-testid="settings-delete-form" className="mt-3 space-y-3">
              {retention ? (
                <div className="rounded-xl border p-4 text-sm" style={{ borderColor: "var(--gf-accent)", background: "var(--gf-surface)" }}>
                  <div className="font-display text-lg" style={{ color: "var(--gf-text)" }}>Before you go…</div>
                  <p className="mt-1" style={{ color: "var(--gf-text-soft)" }}>
                    Would you like to keep your subscription active {retention.days} extra days on us while you think it over?
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button data-testid="settings-delete-accept-offer" onClick={() => requestDelete(true)}
                            className="rounded-md px-4 py-2 text-sm font-medium"
                            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                      Give me {retention.days} days
                    </button>
                    <button data-testid="settings-delete-decline-offer" onClick={() => requestDelete(false)}
                            className="rounded-md border px-4 py-2 text-sm"
                            style={{ borderColor: "#dc2626", color: "#dc2626" }}>
                      No thanks, delete anyway
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Reason</label>
                    <select value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}
                            className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 text-sm"
                            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                            data-testid="settings-delete-reason">
                      {deleteReasonOptions.map(([v, label]) => (
                        <option key={v} value={v}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <textarea placeholder="Anything else you'd like us to know? (optional)"
                            value={deleteNote} onChange={(e) => setDeleteNote(e.target.value)}
                            rows={3}
                            className="w-full bg-transparent border rounded-md px-3 py-2 text-sm"
                            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                            data-testid="settings-delete-note"/>
                  <PasswordInput placeholder="Confirm your password" value={confirmPw}
                         onChange={(e) => setConfirmPw(e.target.value)}
                         className="w-full bg-transparent border rounded-md px-3 py-2 text-sm"
                         style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                         data-testid="settings-delete-password"/>
                  <label className="flex items-start gap-2 text-xs" style={{ color: "var(--gf-text-soft)" }}>
                    <input type="checkbox" checked={confirmChk} onChange={(e) => setConfirmChk(e.target.checked)}
                           data-testid="settings-delete-ack"/>
                    <span>I understand my account will be permanently deleted after 30 days and can only be recovered during that window by signing back in.</span>
                  </label>
                  <div className="flex gap-2">
                    <button data-testid="settings-delete-confirm" onClick={() => requestDelete(false)}
                            disabled={busy === "delete"}
                            className="rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
                            style={{ background: "#dc2626", color: "#fff" }}>
                      {busy === "delete" ? "…" : "Schedule deletion"}
                    </button>
                    <button onClick={() => { setShowDelete(false); setConfirmPw(""); setConfirmChk(false); }}
                            className="rounded-md border px-4 py-2 text-sm"
                            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </SettingsCard>
      </main>
      <Footer />
    </div>
  );
}

function SettingsCard({ title, testid, children }) {
  return (
    <section data-testid={testid} className="mt-6 rounded-2xl border p-5 sm:p-6"
             style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg)" }}>
      <h2 className="font-display text-xl" dangerouslySetInnerHTML={{ __html: title }}
          style={{ color: "var(--gf-text)" }} />
      <div className="mt-2 text-sm space-y-1" style={{ color: "var(--gf-text-soft)" }}>
        {children}
      </div>
    </section>
  );
}

/**
 * Appearance card — three-button theme picker (Light / Dark / System).
 * "System" follows the OS `prefers-color-scheme` in real time — flip your
 * OS between light and dark and the page follows without a reload.
 */
function AppearanceCard() {
  const { mode, theme, setMode } = useTheme();
  const options = [
    {
      key: "light",
      label: "Light",
      desc: "Always use the light theme",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ),
    },
    {
      key: "dark",
      label: "Dark",
      desc: "Always use the dark theme",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      ),
    },
    {
      key: "system",
      label: "System",
      desc: "Match your device settings",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="4" width="20" height="14" rx="2" />
          <path d="M8 22h8M12 18v4" />
        </svg>
      ),
    },
  ];

  return (
    <section data-testid="settings-appearance" className="mt-6 rounded-2xl border p-5 sm:p-6"
             style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg)" }}>
      <h2 className="font-display text-xl" style={{ color: "var(--gf-text)" }}>Appearance</h2>
      <p className="mt-2 text-sm" style={{ color: "var(--gf-text-soft)" }}>
        Choose how BookFreely looks on this device. &ldquo;System&rdquo; follows your device&rsquo;s light/dark preference automatically.
      </p>
      <div
        role="radiogroup"
        aria-label="Theme"
        className="mt-4 grid gap-3 grid-cols-1 sm:grid-cols-3"
      >
        {options.map((opt) => {
          const active = mode === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`appearance-${opt.key}-btn`}
              onClick={() => setMode(opt.key)}
              className="text-left rounded-xl border p-4 transition-colors focus:outline-none focus-visible:ring-2"
              style={{
                borderColor: active ? "var(--gf-accent)" : "var(--gf-border-strong)",
                background: active ? "var(--gf-surface)" : "transparent",
                boxShadow: active ? "0 0 0 3px var(--gf-surface-2)" : "none",
                color: "var(--gf-text)",
              }}
            >
              <div className="flex items-center justify-between">
                <span
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full border"
                  style={{ borderColor: "var(--gf-border)", color: active ? "var(--gf-accent)" : "var(--gf-text-soft)" }}
                >
                  {opt.icon}
                </span>
                {active && (
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5"
                    style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                    data-testid={`appearance-${opt.key}-active-pill`}
                  >
                    Selected
                  </span>
                )}
              </div>
              <div className="mt-3 font-medium" style={{ color: "var(--gf-text)" }}>{opt.label}</div>
              <div className="mt-0.5 text-xs" style={{ color: "var(--gf-text-muted)" }}>{opt.desc}</div>
            </button>
          );
        })}
      </div>
      {mode === "system" && (
        <div className="mt-3 text-xs" data-testid="appearance-system-resolved" style={{ color: "var(--gf-text-muted)" }}>
          Currently applied: <b style={{ color: "var(--gf-text)" }}>{theme}</b> (matched from your device).
        </div>
      )}
    </section>
  );
}

